import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { binanceSignedFetch, getBinancePrecision, FAPI_BASE } from '../utils/binance';
import { userStorage } from '../utils/userStorage';
import { api as authApi } from '../api/client';
import { collectAlertDrawings } from '../utils/alerts';
import { tr } from '@quantum/ui';

const TradingContext = createContext(null);
export const useTrading = () => useContext(TradingContext);

// ─── sessionStorage cache helpers ────────────────────────────────────────────
// sessionStorage survives page refresh (F5 / Ctrl+R) but clears on tab close.
// Perfect for "instant reload" — data shows immediately, then silently refreshes.
const SS = {
  get(key) {
    try {
      const r = JSON.parse(sessionStorage.getItem(key));
      if (!r) return null;
      return Date.now() - r.ts < r.ttl ? r.data : null;
    } catch { return null; }
  },
  set(key, data, ttl = 60_000) {
    try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now(), ttl })); } catch {}
  },
  del(key) {
    try { sessionStorage.removeItem(key); } catch {}
  },
};

const CACHE = {
  positions : 'cyber_pos_v2',
  orders    : 'cyber_ord_v2',
  listenKey : 'cyber_lk_v2',   // TTL = 55 min (Binance expires after 60 min)
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const TradingProvider = ({ children }) => {
  // Initialise from cache immediately — zero perceived delay on reload
  const [activePositions, setActivePositions] = useState(() => SS.get(CACHE.positions) || []);
  const [workingOrders,   setWorkingOrders]   = useState(() => SS.get(CACHE.orders)    || []);
  const [queuedLogic,     setQueuedLogic]     = useState({});

  const listenKeyRef        = useRef(null);
  const wsRef               = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  const pollIntervalRef     = useRef(null);
  const reconnectTimerRef   = useRef(null);   // User Data Stream auto-reconnect timer
  const backoffUntilRef     = useRef(0);   // pause polling until this timestamp after 418/429

  // ── Load positions + orders in parallel ───────────────────────────────────
  const loadInitialData = useCallback(async (api, secret) => {
    try {
      // Fire both requests simultaneously — cuts wait time in half
      const [posData, ordersData] = await Promise.all([
        binanceSignedFetch('/fapi/v2/positionRisk', 'GET', {}, api, secret),
        binanceSignedFetch('/fapi/v1/openOrders',   'GET', {}, api, secret),
      ]);

      const active = posData
        .filter(p => parseFloat(p.positionAmt) !== 0)
        .map(p => ({ ...p, unrealizedProfit: p.unRealizedProfit || p.unrealizedProfit || '0' }));

      setActivePositions(active);
      SS.set(CACHE.positions, active, 90_000); // cache 90 s

      // Algo orders in background — don't block the main update
      let algoOrders = [];
      try {
        const algoData = await binanceSignedFetch('/fapi/v1/openAlgoOrders', 'GET', {}, api, secret);
        algoOrders = algoData.map(o => ({
          orderId : o.algoId || o.orderId,
          symbol  : o.symbol,
          origQty : o.quantity || '0',
          price   : o.price > 0 ? o.price : (o.triggerPrice || o.stopPrice),
          stopPrice: o.triggerPrice || o.stopPrice,
          side    : o.side,
          type    : o.origType || o.type || o.orderType,
          status  : o.algoStatus || o.status,
        }));
      } catch { /* algo orders endpoint may not be available */ }

      const allOrders = [...ordersData, ...algoOrders];
      setWorkingOrders(allOrders);
      SS.set(CACHE.orders, allOrders, 90_000);
      // success → clear any backoff
      backoffUntilRef.current = 0;
    } catch (e) {
      // On IP ban (418) / rate limit (429), back off hard so we don't make it worse.
      const msg = String(e?.message || '');
      if (msg.includes('418') || msg.includes('429')) {
        backoffUntilRef.current = Date.now() + 120_000; // 2-minute cooldown
        console.warn('[TradingContext] Binance rate limit — pausing polling 120s');
      }
      console.error('[TradingContext] loadInitialData failed:', e);
    }
  }, []);

  // ── User Data Stream message handler ─────────────────────────────────────
  const handleWsMessage = useCallback(async (msgStr, api, secret) => {
    try {
      const msg = JSON.parse(msgStr);

      if (msg.e === 'ORDER_TRADE_UPDATE') {
        const order  = msg.o;
        const status = order.X;

        setWorkingOrders(prev => {
          let next;
          if (status === 'NEW' || status === 'PARTIALLY_FILLED') {
            const idx = prev.findIndex(o => o.orderId === order.i);
            if (idx > -1) {
              next = [...prev];
              next[idx] = { ...next[idx], ...order, origQty: order.q, price: order.p, side: order.S, type: order.o };
            } else {
              next = [...prev, { orderId: order.i, symbol: order.s, origQty: order.q, price: order.p, side: order.S, type: order.o, status }];
            }
          } else if (status === 'FILLED' || status === 'CANCELED' || status === 'EXPIRED') {
            next = prev.filter(o => o.orderId !== order.i);
          } else {
            next = prev;
          }
          SS.set(CACHE.orders, next, 90_000);
          return next;
        });

        if (status === 'FILLED') {
          loadInitialData(api, secret);

          // ── Auto break-even: when a TAKE-PROFIT fills (price reached TP1), move
          //    the stop-loss to break-even (+0.1% past entry) on the remaining size.
          const filledType = String(order.o || order.ot || '');
          if (filledType.includes('TAKE_PROFIT')) {
            moveStopToBreakeven(order.s, api, secret);
          }

          setQueuedLogic(prev => {
            const symbolLogic = prev[order.s];
            if (symbolLogic && order.S === symbolLogic.mainSide) {
              console.log(`[TradingContext] FILLED ${order.s} — executing queued SL/TP`);
              executeQueuedLogic(order.s, symbolLogic, api, secret);
              const next = { ...prev };
              delete next[order.s];
              return next;
            }
            return prev;
          });
        }
      }

      if (msg.e === 'ACCOUNT_UPDATE') {
        const positions = msg.a?.P;
        if (positions?.length) {
          setActivePositions(prev => {
            const next = [...prev];
            positions.forEach(p => {
              const idx = next.findIndex(e => e.symbol === p.s && e.positionSide === p.ps);
              if (idx > -1) {
                next[idx] = { ...next[idx], positionAmt: p.pa, entryPrice: p.ep, unrealizedProfit: p.up };
              } else {
                next.push({ symbol: p.s, positionSide: p.ps, positionAmt: p.pa, entryPrice: p.ep, unrealizedProfit: p.up });
              }
            });
            const filtered = next.filter(p => parseFloat(p.positionAmt) !== 0);
            SS.set(CACHE.positions, filtered, 90_000);
            return filtered;
          });
        }
      }
    } catch (e) {
      console.error('[TradingContext] WS message error:', e);
    }
  }, [loadInitialData]);

  // ── Auto break-even ─────────────────────────────────────────────────────────
  // Rule: once price reaches the first take-profit, the stop-loss is moved to
  // break-even at +0.1% past the entry (locking a tiny profit) on the size that's
  // still open. Idempotent — won't lower an SL that's already at/past break-even.
  const moveStopToBreakeven = async (symbol, api, secret) => {
    try {
      const posData = await binanceSignedFetch('/fapi/v2/positionRisk', 'GET', { symbol }, api, secret);
      const pos = (posData || []).find(p => parseFloat(p.positionAmt) !== 0);
      if (!pos) return;                                  // position fully closed already
      const amt   = parseFloat(pos.positionAmt);
      const entry = parseFloat(pos.entryPrice);
      if (!entry || !amt) return;
      const isLong = amt > 0;
      const be = isLong ? entry * 1.001 : entry * 0.999; // break-even + 0.1%

      const prec  = await getBinancePrecision(symbol);
      const fmtP  = v => Number(v).toFixed(prec?.pricePrec ?? 5);

      // Existing stop-loss orders for this symbol (exclude take-profits).
      const open  = await binanceSignedFetch('/fapi/v1/openOrders', 'GET', { symbol }, api, secret);
      const stops = (open || []).filter(o => String(o.type).includes('STOP') && !String(o.type).includes('TAKE_PROFIT'));

      // Already trailed to / past break-even? Don't move it backwards.
      const already = stops.some(o => {
        const sp = parseFloat(o.stopPrice);
        return isLong ? sp >= entry : (sp > 0 && sp <= entry);
      });
      if (already) return;

      for (const o of stops) {
        try { await binanceSignedFetch('/fapi/v1/order', 'DELETE', { symbol, orderId: o.orderId }, api, secret); } catch (_) {}
      }
      await binanceSignedFetch('/fapi/v1/order', 'POST', {
        symbol, side: isLong ? 'SELL' : 'BUY',
        type: 'STOP_MARKET', stopPrice: fmtP(be),
        closePosition: 'true', timeInForce: 'GTE_GTC', workingType: 'MARK_PRICE',
      }, api, secret);
      console.log(`[TradingContext] SL → break-even ${fmtP(be)} for ${symbol}`);
      loadInitialData(api, secret);
    } catch (e) {
      console.error('[TradingContext] moveStopToBreakeven failed:', e);
    }
  };

  // ── SL/TP execution ───────────────────────────────────────────────────────
  const executeQueuedLogic = async (symbol, logic, api, secret) => {
    try {
      const prec       = await getBinancePrecision(symbol);
      const pricePrec  = prec?.pricePrec ?? 5;
      const qtyPrec    = prec?.qtyPrec   ?? 3;
      const fmtP       = v => Number(v).toFixed(pricePrec);
      const fmtQ       = v => Number(v).toFixed(qtyPrec);

      if (logic.sl?.stopPrice > 0) {
        await binanceSignedFetch('/fapi/v1/order', 'POST', {
          symbol, side: logic.mainSide === 'BUY' ? 'SELL' : 'BUY',
          type: 'STOP_MARKET', stopPrice: fmtP(logic.sl.stopPrice),
          closePosition: 'true', timeInForce: 'GTE_GTC', workingType: 'MARK_PRICE',
        }, api, secret);
      }

      for (const tp of (logic.tps || [])) {
        if (tp.stopPrice > 0 && tp.share > 0) {
          await binanceSignedFetch('/fapi/v1/order', 'POST', {
            symbol, side: logic.mainSide === 'BUY' ? 'SELL' : 'BUY',
            type: 'TAKE_PROFIT_MARKET', stopPrice: fmtP(tp.stopPrice),
            quantity: fmtQ((logic.quantity * tp.share) / 100),
            reduceOnly: 'true', workingType: 'MARK_PRICE',
          }, api, secret);
        }
      }
    } catch (e) {
      console.error('[TradingContext] executeQueuedLogic failed:', e);
      alert(tr('Failed to auto-place SL/TP: {e}', { e: e.message }));
    }
  };

  // ── Main setup ────────────────────────────────────────────────────────────
  useEffect(() => {
    const api    = localStorage.getItem('cyber_binance_api');
    const secret = localStorage.getItem('cyber_binance_secret');
    if (!api || !secret) return;

    let active = true;
    let reconnectAttempts = 0;   // resets to 0 on every healthy open

    // Resolve a usable listenKey: reuse the cached one (renew via PUT) unless
    // forceNew is set (the cached key likely expired after a long disconnect).
    const getListenKey = async (forceNew = false) => {
      if (!forceNew) {
        const cached = SS.get(CACHE.listenKey);
        if (cached) {
          try {
            await fetch(`${FAPI_BASE}/fapi/v1/listenKey`, { method: 'PUT', headers: { 'X-MBX-APIKEY': api } });
            SS.set(CACHE.listenKey, cached, 55 * 60_000);
            return cached;
          } catch { /* PUT failed → key expired, fall through to POST a new one */ }
        }
      }
      const res  = await fetch(`${FAPI_BASE}/fapi/v1/listenKey`, { method: 'POST', headers: { 'X-MBX-APIKEY': api } });
      const data = await res.json();
      if (!data.listenKey) throw new Error('No listenKey in response');
      SS.set(CACHE.listenKey, data.listenKey, 55 * 60_000);
      return data.listenKey;
    };

    // Schedule a reconnect with exponential backoff (2s→30s cap). After a few
    // failures we force a brand-new listenKey (the old one has likely expired).
    const scheduleReconnect = () => {
      if (!active) return;
      clearTimeout(reconnectTimerRef.current);
      const delay = Math.min(30_000, 2000 * 2 ** reconnectAttempts);
      reconnectAttempts += 1;
      console.warn(`[TradingContext] User Data Stream down — reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
      reconnectTimerRef.current = setTimeout(() => {
        connect(reconnectAttempts >= 3).catch((e) => {
          console.error('[TradingContext] reconnect failed:', e);
          scheduleReconnect();
        });
      }, delay);
    };

    // Open (or re-open) the User Data Stream and wire auto-reconnect on drop.
    const connect = async (forceNewKey = false) => {
      if (!active) return;
      const listenKey = await getListenKey(forceNewKey);
      if (!active) return;
      listenKeyRef.current = listenKey;

      try { wsRef.current?.close(); } catch { /* ignore */ }   // drop any stale socket

      const ws = new WebSocket(`wss://fstream.binance.com/ws/${listenKey}`);
      wsRef.current = ws;
      ws.onopen = () => {
        reconnectAttempts = 0;                     // healthy again
        console.log('[TradingContext] User Data Stream connected');
        // Resync once on every (re)connect so events missed while down are caught.
        loadInitialData(api, secret);
      };
      ws.onmessage = (e) => handleWsMessage(e.data, api, secret);
      // Only the CURRENT socket may trigger a reconnect (a stale closed socket must not).
      ws.onclose = () => {
        if (active && wsRef.current === ws) {
          console.log('[TradingContext] User Data Stream closed — scheduling reconnect');
          scheduleReconnect();
        }
      };
      ws.onerror = () => {
        if (active && wsRef.current === ws) { try { ws.close(); } catch { /* onclose handles reconnect */ } }
      };
    };

    const setup = async () => {
      // ① Refresh data in background — cached state already shown instantly
      loadInitialData(api, secret);

      // ①b Poll positions/orders as a SAFETY NET. The User Data Stream pushes
      //     changes in real time (and now auto-reconnects), so this just covers
      //     the seconds between a drop and the reconnect. Pauses during backoff.
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        if (!active) return;
        if (Date.now() < backoffUntilRef.current) return; // in cooldown — skip
        loadInitialData(api, secret);
      }, 20000);

      // ② Keep-alive every 30 min — always PUTs the CURRENT listenKey.
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = setInterval(() => {
        const lk = listenKeyRef.current;
        if (!lk) return;
        fetch(`${FAPI_BASE}/fapi/v1/listenKey`, { method: 'PUT', headers: { 'X-MBX-APIKEY': api } })
          .then(() => SS.set(CACHE.listenKey, lk, 55 * 60_000))
          .catch((e) => console.error('[TradingContext] keep-alive error', e));
      }, 30 * 60 * 1000);

      // ③ Connect (auto-reconnects on any drop).
      try { await connect(); }
      catch (e) { console.error('[TradingContext] setup failed:', e); scheduleReconnect(); }
    };

    setup();

    return () => {
      active = false;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(keepAliveIntervalRef.current);
      clearInterval(pollIntervalRef.current);
      // Close WebSocket but DO NOT delete listenKey —
      // it survives page refresh for up to 55 min so the next load reuses it.
      wsRef.current?.close();
    };
  }, [loadInitialData, handleWsMessage]);

  // ── Sync positions/trades/alerts to the Telegram bot backend every 30s ───
  useEffect(() => {
    const sync = () => {
      // Alert drawings from localStorage (persisted by DrawingContext). Shape
      // knowledge lives in utils/alerts.js — an empty list here makes the server
      // deactivate every stored alert, so this must never silently miss them.
      let activeAlerts = [];
      try {
        activeAlerts = collectAlertDrawings(userStorage.getJSON('drawings') || {});
      } catch {}

      authApi('/bot/sync', {
        method: 'POST',
        body: { positions: activePositions, trades: [], alerts: activeAlerts },
      }).catch(() => {}); // silent — bot sync is best-effort
    };
    sync(); // immediate first sync
    const id = setInterval(sync, 30_000);
    return () => clearInterval(id);
  }, [activePositions]);

  const addPendingLogic = useCallback((symbol, logic) =>
    setQueuedLogic(prev => ({ ...prev, [symbol]: logic })), []);

  const removePendingLogic = useCallback((symbol) =>
    setQueuedLogic(prev => { const n = { ...prev }; delete n[symbol]; return n; }), []);

  const value = useMemo(
    () => ({ activePositions, workingOrders, queuedLogic, addPendingLogic, removePendingLogic }),
    [activePositions, workingOrders, queuedLogic, addPendingLogic, removePendingLogic],
  );

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
};
